// pages/ToshbaSubTopicDetail.js
import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchWithAuth } from '../auth';
import Breadcrumbs from '../components/Breadcrumbs';
import { SkeletonCardGrid } from '../components/ui';
import Accordion from '../components/Accordion';
import AdditionalResourcesAccordion from '../components/AdditionalResourcesAccordion';
import { useResourceDetailModal } from '../components/ResourceDetailModalContext';

export default function ToshbaSubTopicDetail() {
  const { domain, id } = useParams();
  const [subtopic, setSubtopic] = useState(null);
  const [files, setFiles] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [resources, setResources] = useState([]);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const domainMap = {
    'mishna': { name: 'משנה', api: 'subtopic-mishna' },
    'gmara': { name: 'גמרא', api: 'subtopic-gmara' },
    'halacha': { name: 'הלכה', api: 'subtopic-halacha' }
  };

  const domainInfo = domainMap[domain] || { name: domain, api: '' };

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/${domainInfo.api}/${id}/`);
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        if (!ignore) {
          setSubtopic(data);
          if (domain === 'mishna' && data.chapters) {
            setChapters(data.chapters.filter(c => c.is_enabled));
          }
          if (domain === 'halacha' && data.books) {
            setBooks(data.books.filter(b => b.is_enabled));
          }
        }

        if (domain === 'mishna' || domain === 'halacha') {
          const filesRes = await fetchWithAuth(`/api/${domainInfo.api}/${id}/files/`);
          if (filesRes.ok) {
            const filesData = await filesRes.json();
            if (!ignore) setFiles(filesData);
          }
        }

        if (domain === 'mishna' && !data.chapters) {
          const chaptersRes = await fetchWithAuth(`/api/subtopic-mishna/${id}/chapters/`);
          if (chaptersRes.ok) {
            const chaptersData = await chaptersRes.json();
            if (!ignore) setChapters(chaptersData.filter(c => c.is_enabled));
          }
        }

        if (domain === 'gmara') {
          const resourcesRes = await fetchWithAuth(`/api/${domainInfo.api}/${id}/resources/`);
          if (resourcesRes.ok) {
            const resourcesData = await resourcesRes.json();
            if (!ignore) setResources(resourcesData);
          }
        }

        if (domain === 'halacha' && !data.books) {
          const booksRes = await fetchWithAuth(`/api/subtopic-halacha/${id}/books/`);
          if (booksRes.ok) {
            const booksData = await booksRes.json();
            if (!ignore) setBooks(booksData.filter(b => b.is_enabled));
          }
        }
      } catch {
        if (!ignore) setError('שגיאה בטעינת התוכן');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [id, domain, domainInfo.api]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="h-6 w-64 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mb-6" />
        <div className="h-8 w-32 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mx-auto mb-8" />
        <SkeletonCardGrid count={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!subtopic) return null;

  const resourceTypeLabels = {
    'TEACHER_GUIDE': 'מדריכים למורה',
    'PRESENTATIONS': 'מצגות',
    'WORKSHEETS': 'דפי עבודה',
    'QUIZZES': 'בחנים',
    'TESTS': 'מבחנים',
    'IMAGES': 'תמונות',
    'VIDEOS': 'סרטונים',
    'GAMES': 'משחקים',
    'PODCASTS': 'פודקאסטים'
  };

  const TYPE_ORDER = [
    'TEACHER_GUIDE',
    'PRESENTATIONS',
    'WORKSHEETS',
    'QUIZZES',
    'TESTS',
    'IMAGES',
    'VIDEOS',
    'GAMES',
    'PODCASTS',
  ];

  const groupedResources = resources.reduce((acc, r) => {
    const type = r.resource_type || 'OTHER';
    if (!acc[type]) acc[type] = [];
    acc[type].push(r);
    return acc;
  }, {});

  const orderedTypeKeys = Object.keys(groupedResources).sort((a, b) => {
    const ia = TYPE_ORDER.indexOf(a);
    const ib = TYPE_ORDER.indexOf(b);
    const iaSafe = ia === -1 ? TYPE_ORDER.length : ia;
    const ibSafe = ib === -1 ? TYPE_ORDER.length : ib;
    return iaSafe - ibSafe;
  });

  return (
    <div className="page-wrapper">
      <Breadcrumbs
        items={[
          { to: '/disciplines', label: 'תחומי דעת' },
          { to: '/toshba/classes', label: 'תושב"ע' },
          { to: `/toshba/classes/${domain}`, label: domainInfo.name },
          { label: subtopic.name },
        ]}
      />

      <div className="toolbar-center">
        <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 dark:text-white">
          {subtopic.name}
        </h2>
      </div>

      <p className="text-center mt-2 mb-6">
        <Link
          to={`/toshba/classes/${domain}`}
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          חזרה ל{domainInfo.name}
        </Link>
      </p>

      {/* Explanation accordion */}
      <Accordion content={subtopic.explanation} className="mb-8" />

      {/* Files section */}
      {(domain === 'mishna' || domain === 'halacha') && files.length > 0 && (
        <section className="mt-8">
          <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">קבצים</h3>
          <div className="grid-autofit">
            {files.map(file => (
              <ResourceCard key={file.id} item={file} domain={domain} />
            ))}
          </div>
        </section>
      )}

      {/* Chapters section (mishna) */}
      {domain === 'mishna' && chapters.length > 0 && (
        <section className="mt-8">
          <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">פרקים</h3>
          <div className="grid-autofit">
            {chapters.map(chapter => (
              <ContentCard
                key={chapter.id}
                to={`/toshba/classes/${domain}/${id}/chapters/${chapter.id}`}
                image={chapter.image}
                title={chapter.name}
              />
            ))}
          </div>
        </section>
      )}

      {/* Resources section (gmara) */}
      {domain === 'gmara' && Object.keys(groupedResources).length > 0 && (
        <section className="mt-8">
          <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">משאבים</h3>
          {orderedTypeKeys.map((type) => {
            const items = groupedResources[type];
            if (!items || items.length === 0) return null;
            return (
            <div key={type} className="mt-6">
              <h4 className="text-lg font-medium text-secondary-800 dark:text-secondary-200 mb-3">
                {resourceTypeLabels[type] || type}
              </h4>
              <div className="grid-autofit">
                {items.map(resource => (
                  <ResourceCard key={resource.id} item={resource} domain={domain} />
                ))}
              </div>
            </div>
            );
          })}
        </section>
      )}

      {/* Books section (halacha) */}
      {domain === 'halacha' && books.length > 0 && (
        <section className="mt-8">
          <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">ספרים</h3>
          <div className="grid-autofit">
            {books.map(book => (
              <ContentCard
                key={book.id}
                to={`/toshba/classes/${domain}/${id}/books/${book.id}`}
                image={book.image}
                title={book.name}
              />
            ))}
          </div>
        </section>
      )}

      {files.length === 0 && chapters.length === 0 && resources.length === 0 && books.length === 0 && (
        <p className="text-center text-secondary-500 dark:text-secondary-400 py-8">
          אין תוכן להצגה.
        </p>
      )}

      {/* Additional Resources Accordion (only for gmara) */}
      {domain === 'gmara' && subtopic.additional_resources && subtopic.additional_resources.length > 0 && (
        <section className="mt-8">
          <AdditionalResourcesAccordion resources={subtopic.additional_resources} />
        </section>
      )}
    </div>
  );
}

function ContentCard({ to, image, title, meta }) {
  return (
    <article className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1">
      <Link to={to} className="block relative overflow-hidden bg-secondary-100 dark:bg-secondary-700" style={{ aspectRatio: '3/2' }}>
        {image ? (
          <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
      </Link>
      <div className="p-4 md:p-5">
        <h4 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
          <Link to={to} className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">{title}</Link>
        </h4>
        {meta && <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">{meta}</p>}
      </div>
    </article>
  );
}

function ResourceCard({ item, domain }) {
  const { openResourceDetail } = useResourceDetailModal();
  const hasFile = !!item.file;
  const isExternal = !!item.url && !hasFile;
  const downloadUrl = hasFile
    ? (domain === 'gmara'
        ? `/api/subtopic-gmara-resources/${item.id}/download/`
        : domain === 'mishna'
          ? `/api/subtopic-mishna-files/${item.id}/download/`
          : domain === 'halacha'
            ? `/api/subtopic-halacha-files/${item.id}/download/`
            : item.file || item.url)
    : item.url || item.file;

  const handleOpen = () => {
    if (!item.use_detail_modal && downloadUrl) {
      window.open(downloadUrl, isExternal ? '_blank' : '_self', isExternal ? 'noopener,noreferrer' : undefined);
      return;
    }

    const modalItem = {
      id: item.id,
      model_type: 'toshba_resource',
      title: item.title || 'קובץ ללא כותרת',
      summary: item.explanation || '',
      explanation: item.explanation || '',
      author: item.author || '',
      subjectLabel: 'תושב״ע',
      typeLabel: 'משאב',
      grades: item.grades || [],
      tags: [],
      downloadUrl,
      date: item.created_at || '',
    };
    openResourceDetail(modalItem);
  };

  return (
    <article className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1">
      <button
        type="button"
        onClick={handleOpen}
        className="block w-full relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
        style={{ aspectRatio: '3/2' }}
      >
        {item.image ? (
          <img src={item.image} alt={item.title || 'קובץ'} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        )}
      </button>
      <div className="p-4 md:p-5">
        <h4 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
          <button
            type="button"
            onClick={handleOpen}
            className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-right w-full text-inherit"
          >
            {item.title || 'קובץ ללא כותרת'}
          </button>
        </h4>
        {item.author && <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">{item.author}</p>}
      </div>
    </article>
  );
}